import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Building2, ChevronDown, Check, Plus, Settings, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StorageImage } from '@/components/ui/StorageImage';

interface CompanySwitcherProps {
  compact?: boolean;
}

export function CompanySwitcher({ compact = false }: CompanySwitcherProps) {
  const { companies, activeCompany, setActiveCompanyId, canCreateCompany } = useCompany();
  const multipleCompanies = useFeature('multiple_companies');
  const navigate = useNavigate();

  const handleAddCompany = () => {
    navigate('/settings?tab=companies&action=new');
  };

  const handleManageCompanies = () => {
    navigate('/settings?tab=companies');
  };

  const handleCompanyClick = (companyId: string) => {
    const isCurrentActive = companyId === activeCompany?.id;
    if (!isCurrentActive && !multipleCompanies.require()) return;
    setActiveCompanyId(companyId);
  };

  const displayName = activeCompany?.name || 'Minha empresa';

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={compact ? 'sm' : 'default'}
            className="relative w-full justify-center gap-2 px-8 font-normal"
          >
            <div className="flex min-w-0 items-center justify-center gap-2">
              {activeCompany?.logo_url ? (
                <StorageImage
                  src={activeCompany.logo_url}
                  alt={activeCompany.name}
                  className="h-5 w-5 shrink-0 rounded object-cover"
                  fallback={<Building2 className="h-4 w-4 shrink-0" />}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Building2 className="h-4 w-4 shrink-0" />
              )}
              <span className="truncate text-sm font-medium">
                {displayName}
              </span>
            </div>
            <ChevronDown className="absolute right-3 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56">
          {companies.map((company) => {
            const isCurrentActive = company.id === activeCompany?.id;
            const isLocked = !multipleCompanies.allowed && !isCurrentActive;

            return (
              <DropdownMenuItem
                key={company.id}
                onClick={() => handleCompanyClick(company.id)}
                className={`gap-2 ${isLocked ? 'opacity-70 justify-between' : ''}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {company.logo_url ? (
                    <StorageImage
                      src={company.logo_url}
                      alt={company.name}
                      className="h-5 w-5 rounded object-cover"
                      fallback={<Building2 className="h-4 w-4 shrink-0" />}
                    />
                  ) : (
                    <Building2 className="h-4 w-4 shrink-0" />
                  )}
                  <span className="truncate">{company.name}</span>
                </div>

                {isCurrentActive && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}

                {isLocked && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-600 bg-amber-50 dark:bg-amber-950/40 gap-1 shrink-0">
                    <Lock className="h-2.5 w-2.5" />
                    PRO
                  </Badge>
                )}
              </DropdownMenuItem>
            );
          })}
          
          <DropdownMenuSeparator />
          
          {(canCreateCompany || multipleCompanies.allowed) && (
            <DropdownMenuItem onClick={handleAddCompany} className="gap-2">
              <Plus className="h-4 w-4" />
              <span>Nova empresa</span>
            </DropdownMenuItem>
          )}
          
          <DropdownMenuItem onClick={handleManageCompanies} className="gap-2">
            <Settings className="h-4 w-4" />
            <span>Gerenciar empresas</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

    </>
  );
}
